import type { ApiClient } from './ApiClient';

// PAY-03 — copie manuelle des 4 méthodes ACCEPTÉES par ce ticket, PAS l'enum
// `PaymentMethod` complet du schéma (qui inclut aussi `CARD`, réservée à un futur
// paiement en ligne réel — voir apps/api/src/lib/payment.ts#CASH_PAYMENT_METHODS
// pour la justification complète).
export type CashPaymentMethod = 'CASH' | 'BANK_TRANSFER' | 'MOBILE_MONEY' | 'CHEQUE';

export const CASH_PAYMENT_METHOD_LABELS: Record<CashPaymentMethod, string> = {
  CASH: 'Espèces',
  BANK_TRANSFER: 'Virement',
  MOBILE_MONEY: 'Mobile Money',
  CHEQUE: 'Chèque',
};

// Forme alignée sur GET /students/:id/invoices (apps/api/src/routes/payments.ts) —
// `amountDue`/`amountPaid`/`balance` en CHAÎNE (jamais `number`), même prudence que
// FeeConfiguration côté montants monétaires.
//
// PAY-10 : `grossAmountDue` ajouté (montant BRUT avant réduction, `amountDue`
// reste le montant NET réellement dû, INCHANGÉ) — "montant brut, réduction(s)
// listée(s), montant net — les trois toujours visibles" (exigence explicite
// du ticket). Le détail itemisé des réductions vit à part, voir
// `getInvoiceDiscounts()` ci-dessous.
export interface InvoiceDue {
  id: string;
  // PAY-19 : numéro de facture lisible (FAC-{année}-{6 chiffres}) — `null`
  // uniquement pour une facture antérieure à ce ticket jamais rétro-remplie
  // en pratique (voir apps/api, schema.prisma) ou une fixture de test.
  invoiceNumber: string | null;
  feeType: string;
  period: string | null;
  grossAmountDue: string;
  amountDue: string;
  amountPaid: string;
  balance: string;
  dueDate: string;
  status: string;
}

export interface StudentInvoicesResponse {
  student: { id: string; firstName: string; lastName: string; matricule: string; currentClassName: string | null };
  invoices: InvoiceDue[];
}

export function getStudentInvoicesDue(client: ApiClient, studentId: string, accessToken: string): Promise<StudentInvoicesResponse> {
  return client.get<StudentInvoicesResponse>(`/students/${studentId}/invoices`, { accessToken });
}

// Maquette "Eleves.dc.html" (tiroir de détail, section "Historique de paiement —
// scolarité") — DISTINCT de `getStudentInvoicesDue()` ci-dessus : celui-ci ne
// renvoie que les factures EN ATTENTE (flux caisse, PAY-03) ; celui-ci renvoie
// l'échéancier TUITION COMPLET (payées comprises) de l'inscription courante, avec
// un statut global dérivé — voir apps/api/src/lib/studentPaymentStatus.ts.
// Réutilise `InvoiceDue` tel quel (même forme exacte que `selectInvoiceRecap`
// côté backend, voir plus haut).
export interface StudentPaymentStatusResponse {
  overallStatus: 'PAID' | 'PARTIAL' | 'LATE' | null;
  invoices: InvoiceDue[];
}

export function getStudentPaymentStatus(
  client: ApiClient,
  studentId: string,
  accessToken: string,
): Promise<StudentPaymentStatusResponse> {
  return client.get<StudentPaymentStatusResponse>(`/students/${studentId}/payment-status`, { accessToken });
}

export interface RecordPaymentPayload {
  invoiceId: string;
  amount: string;
  method: CashPaymentMethod;
  // Validation en DEUX TEMPS (même endpoint, même pattern que `confirmDuplicate`
  // ENR-01/PAR-02) : `confirm` absent/`false` -> récapitulatif SANS écriture ;
  // `confirm: true` -> écriture réelle.
  confirm?: boolean;
  // PAY-02 : REQUIS dès que `confirm: true` — UUID généré côté client
  // (`crypto.randomUUID()`), UNE SEULE FOIS par tentative de paiement,
  // réutilisé tel quel pour toute nouvelle tentative de la MÊME intention de
  // paiement (double-clic, retry après erreur réseau). Une clé déjà vue
  // renvoie la transaction ORIGINALE ; réutilisée avec des paramètres
  // différents, un 409 explicite. Optionnel/ignoré sur un récapitulatif (qui
  // n'écrit rien).
  idempotencyKey?: string;
}

export interface PaymentRecap {
  kind: 'recap';
  student: { id: string; firstName: string; lastName: string };
  invoice: InvoiceDue;
  amount: string;
  method: CashPaymentMethod;
  balanceAfter: string;
  // `true` si le montant saisi dépasse le solde dû — un AVERTISSEMENT, jamais un
  // blocage (règle métier explicite du ticket : "flexibilité terrain assumée").
  warning: boolean;
}

export interface PaymentConfirmed {
  kind: 'confirmed';
  payment: { id: string; amount: string; method: CashPaymentMethod; status: string; createdAt: string };
  invoice: InvoiceDue;
  student: { id: string; firstName: string; lastName: string };
  warning: boolean;
}

export type RecordPaymentResponse = PaymentRecap | PaymentConfirmed;

export function recordPayment(client: ApiClient, payload: RecordPaymentPayload, accessToken: string): Promise<RecordPaymentResponse> {
  return client.post<RecordPaymentResponse>('/payments', payload, { accessToken });
}

// PAY-08 — historique des paiements COMPLETED d'un élève (GET /students/:id/payments),
// point d'entrée UX pour créer un avoir. `netAmount` = `amount` déjà déduit des avoirs
// existants (calculé côté serveur, jamais recalculé côté client).
export interface PaymentAdjustmentSummary {
  id: string;
  amount: string;
  reason: string;
  createdAt: string;
}

export interface PaymentHistoryItem {
  id: string;
  amount: string;
  method: CashPaymentMethod;
  status: string;
  createdAt: string;
  invoice: { id: string; invoiceNumber: string | null; feeType: string; period: string | null };
  netAmount: string;
  adjustments: PaymentAdjustmentSummary[];
}

export interface StudentPaymentsResponse {
  student: { id: string; firstName: string; lastName: string };
  payments: PaymentHistoryItem[];
}

export function getStudentPaymentHistory(client: ApiClient, studentId: string, accessToken: string): Promise<StudentPaymentsResponse> {
  return client.get<StudentPaymentsResponse>(`/students/${studentId}/payments`, { accessToken });
}

export interface CreatePaymentAdjustmentPayload {
  amount: string;
  reason: string;
  // Même pattern recap/confirm que `recordPayment` ci-dessus.
  confirm?: boolean;
}

export interface PaymentAdjustmentRecap {
  kind: 'recap';
  payment: { id: string; amount: string; method: CashPaymentMethod };
  amount: string;
  reason: string;
  remainingAdjustableAfter: string;
}

export interface PaymentAdjustmentConfirmed {
  kind: 'confirmed';
  adjustment: PaymentAdjustmentSummary;
  payment: { id: string; amount: string };
  invoice: InvoiceDue;
}

export type CreatePaymentAdjustmentResponse = PaymentAdjustmentRecap | PaymentAdjustmentConfirmed;

export function createPaymentAdjustment(
  client: ApiClient,
  paymentId: string,
  payload: CreatePaymentAdjustmentPayload,
  accessToken: string,
): Promise<CreatePaymentAdjustmentResponse> {
  return client.post<CreatePaymentAdjustmentResponse>(`/payments/${paymentId}/adjustments`, payload, { accessToken });
}

// PAY-10 — détail ITEMISÉ des réductions appliquées à UNE facture précise
// (GET /invoices/:id/discounts), consommé quand une facture précise est
// affichée (écran récapitulatif de paiement), pas pour une liste entière.
export interface InvoiceDiscountApplication {
  id: string;
  discountTypeLabel: string;
  mode: 'FIXED' | 'PERCENTAGE';
  amount: string;
}

export interface InvoiceDiscountsResponse {
  invoiceId: string;
  grossAmountDue: string;
  amountDue: string;
  discounts: InvoiceDiscountApplication[];
}

export function getInvoiceDiscounts(client: ApiClient, invoiceId: string, accessToken: string): Promise<InvoiceDiscountsResponse> {
  return client.get<InvoiceDiscountsResponse>(`/invoices/${invoiceId}/discounts`, { accessToken });
}

// PAY-13 — panneau "Mes encaissements du jour" (Payments.tsx, UX-16) :
// encaissements dont `collectedBy` est L'UTILISATEUR COURANT, créés depuis
// le début de la journée calendaire — combine les paiements de FACTURE
// (`type: 'INVOICE'`, PAY-03) et de FOURNITURES (`type: 'SUPPLY_ORDER'`,
// FOU-02, qui n'écrit jamais dans `Payment`) dans une seule liste triée du
// plus récent au plus ancien.
export interface TodayCollectionItem {
  id: string;
  type: 'INVOICE' | 'SUPPLY_ORDER';
  amount: string;
  method: CashPaymentMethod;
  occurredAt: string;
  student: { id: string; firstName: string; lastName: string };
  feeType: string | null;
  // UX-58 : `YYYY-MM`, `null` pour un item SUPPLY_ORDER ou une facture sans
  // notion de période (REGISTRATION/CANTEEN/TRANSPORT/OTHER).
  period: string | null;
}

export interface TodayCollectionsResponse {
  payments: TodayCollectionItem[];
  total: string;
}

export function getMyCollectionsToday(client: ApiClient, accessToken: string): Promise<TodayCollectionsResponse> {
  return client.get<TodayCollectionsResponse>('/payments/mine/today', { accessToken });
}

// UX-54 — GET /payments/history : liste PLATE, chronologique, SCOPÉE PAR
// RÔLE côté serveur (CASHIER -> ses propres encaissements uniquement,
// SCHOOL_ADMIN/ADMIN_FINANCIER -> tout le tenant). AUCUN paramètre de portée
// n'existe côté client — le scope est décidé UNIQUEMENT par le rôle du
// membre authentifié, jamais transmis ici. Étend `TodayCollectionItem`
// (PAY-13, ci-dessus) de `collectedByName` (colonne "Encaissé par") et des
// données nécessaires à "Créer un avoir" (PAY-08) pour un item `INVOICE`
// UNIQUEMENT — une commande de fournitures n'a jamais de `Payment`, donc
// jamais d'avoir possible dessus.
export interface PaymentHistoryEntry {
  id: string;
  type: 'INVOICE' | 'SUPPLY_ORDER';
  amount: string;
  method: CashPaymentMethod;
  occurredAt: string;
  student: { id: string; firstName: string; lastName: string };
  collectedByName: string | null;
  invoice: { id: string; invoiceNumber: string | null; feeType: string; period: string | null } | null;
  netAmount: string | null;
  adjustments: PaymentAdjustmentSummary[];
}

export interface PaymentHistoryPageResponse {
  payments: PaymentHistoryEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export function getPaymentHistory(
  client: ApiClient,
  accessToken: string,
  page = 1,
  pageSize = 20,
  // Raccourci `Caisse.tsx` ("Voir l'historique des paiements") — FILTRE
  // appliqué EN PLUS du scope par rôle, jamais une portée séparée (voir
  // apps/api/src/routes/payments.ts).
  studentId?: string,
): Promise<PaymentHistoryPageResponse> {
  return client.get<PaymentHistoryPageResponse>('/payments/history', {
    accessToken,
    query: { page, pageSize, studentId },
  });
}
